import * as React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  LinearProgress,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { withTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import { useVehiclesReport } from '../hooks/useReportsApi';

const COLORS = ['#3182ce', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

function VehiclesTab({ t, dateParams }) {
  const { data, loading, error } = useVehiclesReport(dateParams);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  // Get top makes for the bar chart (limit to top 10)
  const topMakes = (data?.by_make || []).slice(0, 10);

  return (
    <Box>
      {/* Export Buttons */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="vehicles" params={dateParams} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={DirectionsCarIcon}
            label={t('reports.vehicles.total')}
            value={data?.total}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={CategoryIcon}
            label={t('reports.vehicles.uniqueMakes')}
            value={data?.unique_makes}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={DirectionsCarIcon}
            label={t('reports.vehicles.mostCommon')}
            value={data?.most_common_make || '-'}
            loading={loading}
            format="text"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={CalendarTodayIcon}
            label={t('reports.vehicles.avgYear')}
            value={data?.avg_year}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts Row - only show when there are vehicles */}
      {!loading && data?.total > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Top Makes Bar Chart */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('reports.vehicles.byMake')}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topMakes} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="make" type="category" width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3182ce" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Year Range Pie */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('reports.vehicles.byYearRange')}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={(data?.by_year_range || []).filter(item => item.count > 0)}
                    cx="50%"
                    cy="45%"
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="range_name"
                  >
                    {(data?.by_year_range || []).filter(item => item.count > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value, entry) => `${value} (${entry.payload?.count ?? 0})`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Makes Table */}
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.vehicles.allMakes')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.vehicles.make')}</TableCell>
                  <TableCell align="right">{t('reports.vehicles.count')}</TableCell>
                  <TableCell>{t('reports.vehicles.percent')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.by_make || []).map((make, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>{make.make}</Typography>
                    </TableCell>
                    <TableCell align="right">{make.count}</TableCell>
                    <TableCell sx={{ width: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={make.percent}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: COLORS[index % COLORS.length],
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                          {make.percent.toFixed(1)}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {(!data?.by_make || data.by_make.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                      {t('reports.noData')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default withTranslation()(VehiclesTab);
